import React, { useEffect, useMemo } from 'react';
import TooltipBox from '@wconcept/wconcept-ui-renewal/src/components/molecules/TooltipBox';
import LpFilterBarV2 from '../LpFilterBarV2';
import ProductSortBar from '../../display/ProductSortBar';
import { TopSectionProps } from '../types';

const TopSection = React.memo<TopSectionProps>(
  ({
    filterBarList,
    lastFilterTabRef,
    selectedFilters,
    originalFilters,
    isCategoryMenu,
    dcRateGroupInfo,
    dcRateValue,
    onResetFilter,
    onFilterSubmit,
    onDcRateGroupChange,
    onClickPromoBtn,
    onFilterClose,
    getRequestProductAggByType,
    getRequestProductCount,
    isMobile,
    sort,
    defalutSort,
    sorts,
    tooltip,
    useFreeDelivery,
    usePreOrder,
    useSoldOut,
    isNativeAppBoth,
    onSoldoutChange,
    onChangePreOrderOrFreeDelivery,
    onSortChange,
    hideFilterArea,
    hideFilterOptionCounts,
    hiddenSelectedFilterTypes,
    sortingBarToggleItem,
    disableFilterScrollPin,
  }: TopSectionProps) => {
    // 필터바 스크롤 fixed
    useEffect(() => {
      if (disableFilterScrollPin) return undefined;

      let pinned = false;
      let pinnedFilter = false;

      const handleScroll = () => {
        const $fl = document.querySelector('.lp-filter-bar');
        const $pin = $fl?.querySelector('.pin-area');
        const $quick = $fl?.querySelector('.area-quick');

        if (!($fl instanceof HTMLElement)) return;

        if (!isCategoryMenu) {
          // 고정 기준선 : 화면 상단에 붙어 있는 헤더/GNB의 실제 하단.
          // 스크롤 방향에 따라 오르내리므로(transition 0.3s) 상수 대신 매번 잰다.
          const pinLine = [
            '#header .inner-header',
            '#main-gnb-bar',
            '.sub-gnb-list',
          ].reduce((line, selector) => {
            const $el = document.querySelector(selector);
            return $el
              ? Math.max(line, $el.getBoundingClientRect().bottom)
              : line;
          }, 0);

          const innerTop = $quick
            ? $quick.getBoundingClientRect().bottom
            : $fl.getBoundingClientRect().top;
          const isPinned = innerTop <= pinLine;

          if (isPinned) {
            const placeholder =
              ($quick?.getBoundingClientRect().height || 0) +
              ($pin?.getBoundingClientRect().height || 0);
            $fl.style.height = `${placeholder}px`;
          } else {
            $fl.style.height = '';
          }
          $fl.classList.toggle('is-pinned', isPinned);
          return;
        }

        const $am = document.querySelector('.area-menu');
        const $cm = document.querySelector('.area-menu .category-menubar');

        if (!($am instanceof HTMLElement)) return;

        // 고정 요소가 내려앉는 자리 = 화면에 남는 GNB 바의 높이.
        // 앱 웹뷰에는 GNB가 없으므로 0이다. CSS는 이 값을 --sale-pin-top으로 받는다
        const $gnb = document.querySelector('#main-gnb-bar');
        const pinTop = $gnb ? $gnb.clientHeight : 0;
        // 그 자리가 지금 화면에 보이는 높이. 헤더가 내려와 있으면 그만큼 아래다
        // (고정 요소도 같은 거리만큼 transform으로 따라 내려간다)
        const pinNow = $gnb
          ? $gnb.getBoundingClientRect().bottom -
            (parseFloat(getComputedStyle($gnb).borderBottomWidth) || 0)
          : 0;
        const categoryHeight = $cm?.getBoundingClientRect().height || 0;

        $am.style.setProperty('--sale-pin-top', `${pinTop}px`);

        // 고정은 내려앉을 자리(pinTop)에서 걸고, 해제는 지금 보이는 자리(pinNow)에서 푼다.
        // 두 선이 다른 구간에서는 상태를 그대로 둬 스크롤 방향만 바뀌어도 뒤집히지 않는다
        // 1단계 카테고리 탭 : 자리(.area-menu 상단)가 기준선에 닿는 시점
        const areaTop = $am.getBoundingClientRect().top;
        const isPinned = pinned ? areaTop <= pinNow : areaTop <= pinTop;

        // 2단계 필터바 .pin-area : 1행 아래가 고정된 카테고리 탭 밑에 닿는 시점
        // (1행이 없으면 필터바 자리를 그대로 쓴다)
        const innerTop = $quick
          ? $quick.getBoundingClientRect().bottom
          : $fl.getBoundingClientRect().top;
        const isPinnedFilter = pinnedFilter
          ? innerTop <= pinNow + categoryHeight
          : innerTop <= pinTop + categoryHeight;

        pinned = isPinned;
        pinnedFilter = isPinnedFilter;

        if (isPinned) {
          const quickHeight = $quick?.getBoundingClientRect().height || 0;
          const pinHeight = $pin?.getBoundingClientRect().height || 0;
          $am.style.height = `${categoryHeight + quickHeight + pinHeight}px`;
        } else {
          $am.style.height = '';
        }

        $am.classList.toggle('is-pinned', isPinned);
        $am.classList.toggle('is-pinned-filter', isPinnedFilter);
      };

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
      };

      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', onScroll);
      };
    }, [disableFilterScrollPin, isCategoryMenu]);

    const sortsList = useMemo(
      () =>
        sorts?.map(item => {
          if (item.code === 'WCK' && tooltip) {
            return {
              ...item,
              tooltip: (
                <TooltipBox
                  type="left"
                  text="상품의 판매 실적, 고객 선호도 등을 점수화하여 정렬됩니다."
                />
              ),
            };
          }
          if (item.code === 'MD' && tooltip) {
            return {
              ...item,
              tooltip: (
                <TooltipBox
                  type="left"
                  text="MD가 직접 추천하는 상품순으로 정렬됩니다."
                />
              ),
            };
          }
          return item;
        }) || [],
      [sorts, tooltip]
    );

    return (
      <>
        {!hideFilterArea && (
          <LpFilterBarV2
            list={filterBarList}
            lastFilterTabRef={lastFilterTabRef}
            selectedFilterList={selectedFilters}
            originalFilters={originalFilters}
            onResetFilter={onResetFilter}
            onFilterSubmit={onFilterSubmit}
            requestFilter={getRequestProductAggByType}
            requestCount={getRequestProductCount}
            onClickPromoBtn={onClickPromoBtn}
            onFilterClose={onFilterClose}
            hideFilterOptionCounts={hideFilterOptionCounts}
            hiddenSelectedFilterTypes={hiddenSelectedFilterTypes}
            isCategoryMenu={isCategoryMenu}
            dcRateValue={dcRateValue}
            dcRateGroupInfo={dcRateGroupInfo}
            onDcRateGroupChange={onDcRateGroupChange}
          />
        )}
        <ProductSortBar
          isMobile={!!isMobile}
          value={sort || defalutSort}
          sorts={sortsList}
          usePreOrder={hideFilterArea ? false : usePreOrder}
          useFreeDelivery={hideFilterArea ? false : useFreeDelivery}
          useSoldOut={useSoldOut}
          isPreOrder={
            selectedFilters?.some(
              v =>
                v.filterType === 'productType' && v.code === 'excludePreOrder'
            ) || false
          }
          isFreeDelivery={
            selectedFilters?.some(
              v => v.filterType === 'benefit' && v.code === 'freeDelivery'
            ) || false
          }
          isSoldOut={
            selectedFilters?.some(
              v =>
                v.filterType === 'productType' && v.code === 'excludeOutOfStock'
            ) || false
          }
          onSoldoutChange={onSoldoutChange}
          onChangePreOrderOrFreeDelivery={onChangePreOrderOrFreeDelivery}
          isNativeAppBoth={isNativeAppBoth}
          onChangeSort={onSortChange}
          toggleItem={sortingBarToggleItem}
        />
      </>
    );
  }
);

TopSection.displayName = 'TopSection';

export default TopSection;
